import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  ArrowLeft,
  FileText,
  TestTube,
  Scissors,
  Calendar,
  Stethoscope,
  User,
  Eye,
  Plus,
  Clock,
  AlertCircle,
  Activity,
  Heart,
  Thermometer,
  Pencil
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DoctorSearchableSelect } from '@/components/common/DoctorSearchableSelect';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ipdApi } from '@/api/ipd';
import { IPDAdmission } from '@/types/ipd';
import { ensureDoctorPrefix } from '@/lib/doctorName';
import IPDVisitForm from '@/components/ipd/IPDVisitForm';
import ViewTestResult from '@/pages/lab/ViewTestResult';
import { labApi } from '@/api/lab';
import { useQuery } from '@tanstack/react-query';
import { LabTestSearch } from '@/components/LabTestSearch';

function maskPhone(phone: string | null | undefined): string {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  return digits.slice(0, 3) + '*'.repeat(digits.length - 3);
}

export default function IPDPatientDetail() {
  const { admissionId } = useParams<{ admissionId: string }>();
  const navigate = useNavigate();
  const fromReceptionist = window.location.pathname.includes('/receptionist/');
  const backUrl = fromReceptionist ? '/receptionist/ipd-queue' : '/doctor/ipd-management';
  const [isLoading, setIsLoading] = useState(true);
  const [isVisitFormOpen, setIsVisitFormOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedVisit, setSelectedVisit] = useState<any | null>(null);
  const [isVisitDetailOpen, setIsVisitDetailOpen] = useState(false);
  const [selectedTestForView, setSelectedTestForView] = useState<{ id: string; name: string } | null>(null);
  const [isViewTestResultOpen, setIsViewTestResultOpen] = useState(false);
  const [selectedIPDTest, setSelectedIPDTest] = useState<any | null>(null);
  const [isViewIPDTestOpen, setIsViewIPDTestOpen] = useState(false);
  const [isAddTestFormOpen, setIsAddTestFormOpen] = useState(false);
  const [isAddSurgeryFormOpen, setIsAddSurgeryFormOpen] = useState(false);
  const [surgeryForm, setSurgeryForm] = useState({
    surgeryName: '',
    surgeryCode: '',
    category: '',
    priority: 'ROUTINE' as 'ROUTINE' | 'URGENT' | 'STAT' | 'EMERGENCY',
    status: 'NOT_CONFIRMED' as 'NOT_CONFIRMED' | 'CONFIRMED' | 'NOT_REQUIRED' | 'CANCELLED',
    scheduledAt: '',
    estimatedDuration: '',
    procedureDescription: '',
    preoperativeDiagnosis: '',
    postoperativeDiagnosis: '',
    anesthesiaType: '',
    anesthesiaNotes: '',
    bloodTransfusion: false,
    bloodLoss: '',
    bloodUnits: '',
    primarySurgeonId: '',
    primarySurgeon: '',
    assistantSurgeon: '',
    anesthesiologist: '',
    scrubNurse: '',
    circulatingNurse: '',
    surgeryCost: '',
    anesthesiaCost: '',
    totalCost: '',
  });
  const [surgerySubmitting, setSurgerySubmitting] = useState(false);
  const [confirmingSurgeryId, setConfirmingSurgeryId] = useState<string | null>(null);
  const [selectedSurgery, setSelectedSurgery] = useState<any | null>(null);
  const [isSurgeryDetailOpen, setIsSurgeryDetailOpen] = useState(false);
  const [editingSurgery, setEditingSurgery] = useState<any | null>(null);
  const [isEditSurgeryFormOpen, setIsEditSurgeryFormOpen] = useState(false);
  const [editSurgeryForm, setEditSurgeryForm] = useState({
    surgeryName: '',
    surgeryCode: '',
    category: '',
    priority: 'ROUTINE' as 'ROUTINE' | 'URGENT' | 'STAT' | 'EMERGENCY',
    status: 'NOT_CONFIRMED' as 'NOT_CONFIRMED' | 'CONFIRMED' | 'NOT_REQUIRED' | 'CANCELLED',
    scheduledAt: '',
    estimatedDuration: '',
    procedureDescription: '',
    preoperativeDiagnosis: '',
    postoperativeDiagnosis: '',
    anesthesiaType: '',
    anesthesiaNotes: '',
    bloodTransfusion: false,
    bloodLoss: '',
    bloodUnits: '',
    primarySurgeonId: '',
    primarySurgeon: '',
    assistantSurgeon: '',
    anesthesiologist: '',
    scrubNurse: '',
    circulatingNurse: '',
    surgeryCost: '',
    anesthesiaCost: '',
    totalCost: '',
  });
  const [editSurgerySubmitting, setEditSurgerySubmitting] = useState(false);

  const handleConfirmSurgery = async (surgeryId: string) => {
    setConfirmingSurgeryId(surgeryId);
    try {
      await ipdApi.updateIPDSurgery(surgeryId, { status: 'CONFIRMED' });
      toast.success('Surgery confirmed');
      fetchPatientDetails();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to confirm surgery');
    } finally {
      setConfirmingSurgeryId(null);
    }
  };

  const openEditSurgeryForm = (s: any) => {
    const d = s.scheduledAt ? new Date(s.scheduledAt) : null;
    setEditSurgeryForm({
      surgeryName: s.surgeryName ?? '',
      surgeryCode: s.surgeryCode ?? '',
      category: s.category ?? '',
      priority: (s.priority ?? 'ROUTINE') as 'ROUTINE' | 'URGENT' | 'STAT' | 'EMERGENCY',
      status: (s.status ?? 'NOT_CONFIRMED') as 'NOT_CONFIRMED' | 'CONFIRMED' | 'NOT_REQUIRED' | 'CANCELLED',
      scheduledAt: d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '',
      estimatedDuration: s.estimatedDuration != null ? String(s.estimatedDuration) : '',
      procedureDescription: s.procedureDescription ?? '',
      preoperativeDiagnosis: s.preoperativeDiagnosis ?? '',
      postoperativeDiagnosis: s.postoperativeDiagnosis ?? '',
      anesthesiaType: s.anesthesiaType ?? '',
      anesthesiaNotes: s.anesthesiaNotes ?? '',
      bloodTransfusion: !!s.bloodTransfusion,
      bloodLoss: s.bloodLoss != null ? String(s.bloodLoss) : '',
      bloodUnits: s.bloodUnits != null ? String(s.bloodUnits) : '',
      primarySurgeonId: (s.primarySurgeonStaff?.id ?? s.primarySurgeonId) ?? '',
      primarySurgeon: (s.primarySurgeonStaff?.name ?? s.primarySurgeon) ?? '',
      assistantSurgeon: s.assistantSurgeon ?? '',
      anesthesiologist: s.anesthesiologist ?? '',
      scrubNurse: s.scrubNurse ?? '',
      circulatingNurse: s.circulatingNurse ?? '',
      surgeryCost: s.surgeryCost != null ? String(s.surgeryCost) : '',
      anesthesiaCost: s.anesthesiaCost != null ? String(s.anesthesiaCost) : '',
      totalCost: s.totalCost != null ? String(s.totalCost) : '',
    });
    setEditingSurgery(s);
    setIsEditSurgeryFormOpen(true);
  };

  const { data: doctorsRes } = useQuery({
    queryKey: ['doctors-for-surgery'],
    queryFn: () => ipdApi.getDoctors(),
    enabled: isAddSurgeryFormOpen || isEditSurgeryFormOpen,
  });
  const doctors = (doctorsRes?.data as { data?: { id: string; name: string; specialisation?: string }[] })?.data ?? [];

  const [patientData, setPatientData] = useState<{
    admission: IPDAdmission;
    ipdDocuments: any[];
    ipdVisits: any[];
    ipdLabTests: any[];
    ipdSurgeries: any[];
    opdLabTests: any[];
    opdSurgeries: any[];
    patientDocuments: any[];
  } | null>(null);

  // Fetch patient details
  const fetchPatientDetails = async () => {
    if (!admissionId) return;
    
    try {
      setIsLoading(true);
      const response = await ipdApi.getPatientDetails(admissionId);
      setPatientData(response.data.data);
    } catch (error) {
      console.error('Error fetching patient details:', error);
      toast.error('Failed to fetch patient details');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPatientDetails();
  }, [admissionId]);

  // Pre-fill Add Surgery form from admission (package/surgery name) when dialog opens
  useEffect(() => {
    if (isAddSurgeryFormOpen && patientData?.admission) {
      const adm = patientData.admission as { surgeryName?: string | null };
      setSurgeryForm((prev) => ({
        ...prev,
        surgeryName: adm.surgeryName ?? prev.surgeryName,
      }));
    }
  }, [isAddSurgeryFormOpen, patientData?.admission?.surgeryName]);

  const formatDate = (dateString: string | Date | null | undefined) => {
    if (!dateString) return 'Invalid Date';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED':
      case 'ADMITTED':
      case 'CONFIRMED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
      case 'NOT_CONFIRMED':
        return 'bg-yellow-100 text-yellow-800';
      case 'CANCELLED':
      case 'NOT_REQUIRED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatSurgeryStatus = (status: string) =>
    status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const handleVisitAdded = () => {
    fetchPatientDetails();
    setIsVisitFormOpen(false);
  };


  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(backUrl)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading patient details...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!patientData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(backUrl)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
        <Card>
          <CardContent className="p-8">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Patient details not found</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { admission, ipdDocuments, ipdVisits, ipdLabTests, ipdSurgeries, opdLabTests, opdSurgeries, patientDocuments } = patientData;
  const patient = admission.queue.patient;

  // Group visits by date
  const visitsByDate = ipdVisits.reduce((acc: any, visit: any) => {
    if (!visit) return acc;
    // Handle both visitDate and createdAt fields
    const visitDate = visit.visitDate || visit.createdAt;
    if (!visitDate) return acc;
    try {
      const dateObj = new Date(visitDate);
      if (isNaN(dateObj.getTime())) return acc;
      const date = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      if (!acc[date]) acc[date] = [];
      acc[date].push(visit);
    } catch (error) {
      console.error('Error grouping visit by date:', error, visit);
    }
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(backUrl)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold tabular-nums text-artis-brown-dark">{patient.name}</h1>
            <p className="text-sm text-artis-slate">
              {admission.thirdPartyHospitalName
                ? `${admission.queue.ipdNumber} • Third party: ${admission.thirdPartyHospitalName}`
                : `${admission.queue.ipdNumber} • ${admission.wardType} • Room ${admission.roomNumber || 'N/A'} • Bed ${admission.bedNumber || 'N/A'}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={getStatusColor(admission.status)}>
            {admission.status}
          </Badge>
        </div>
      </div>

      {/* Patient Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-600" />
            Patient Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-artis-slate">UHID</div>
              <div className="font-medium">{patient.uhid}</div>
            </div>
            <div>
              <div className="text-sm text-artis-slate">Age</div>
              <div className="font-medium">
                {patient.dob ? `${new Date().getFullYear() - new Date(patient.dob).getFullYear()} years` : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-sm text-artis-slate">Gender</div>
              <div className="font-medium">{patient.gender}</div>
            </div>
            <div>
              <div className="text-sm text-artis-slate">Phone</div>
              <div className="font-medium">{maskPhone(patient.phone)}</div>
            </div>
            <div>
              <div className="text-sm text-artis-slate">Admission Date</div>
              <div className="font-medium">{formatDate(admission.admissionDate)}</div>
            </div>
            <div>
              <div className="text-sm text-artis-slate">Assigned Doctor</div>
              <div className="font-medium">
                {admission.assignedDoctor?.name || 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-sm text-artis-slate">Chief Complaint</div>
              <div className="font-medium">{admission.chiefComplaint || 'N/A'}</div>
            </div>
            <div>
              <div className="text-sm text-artis-slate">Insurance</div>
              <div className="font-medium">{admission.insuranceType || 'N/A'}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="opd-tests">OPD Tests</TabsTrigger>
          <TabsTrigger value="opd-surgeries">OPD Surgeries</TabsTrigger>
          <TabsTrigger value="ipd-visits">IPD Visits</TabsTrigger>
          <TabsTrigger value="ipd-tests">IPD Tests</TabsTrigger>
          <TabsTrigger value="ipd-surgeries">IPD Surgeries</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{ipdDocuments.length + patientDocuments.length}</div>
                <p className="text-sm text-artis-slate">Total documents</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TestTube className="h-5 w-5" />
                  Tests
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{opdLabTests.length + ipdLabTests.length}</div>
                <p className="text-sm text-artis-slate">OPD: {opdLabTests.length} | IPD: {ipdLabTests.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Scissors className="h-5 w-5" />
                  Surgeries
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{opdSurgeries.length + ipdSurgeries.length}</div>
                <p className="text-sm text-artis-slate">OPD: {opdSurgeries.length} | IPD: {ipdSurgeries.length}</p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Visits */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Recent Visits
                </span>
                <Button size="sm" onClick={() => setIsVisitFormOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Visit
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ipdVisits.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No visits recorded yet</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(visitsByDate).slice(0, 3).map(([date, visits]: [string, any]) => (
                    <div key={date} className="border-l-2 border-blue-500 pl-4">
                      <div className="font-medium text-gray-900">{date}</div>
                      {visits.map((visit: any) => (
                        <div 
                          key={visit.id} 
                          className="mt-2 text-sm text-gray-600 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                          onClick={() => {
                            setSelectedVisit(visit);
                            setIsVisitDetailOpen(true);
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            {formatDate(visit.visitDate || visit.createdAt)}
                            {visit.doctor && (
                              <>
                                <span className="mx-1">•</span>
                                <span className="text-blue-600">{ensureDoctorPrefix(visit.doctor.name)}</span>
                              </>
                            )}
                          </div>
                          <p className="mt-1 line-clamp-2">{visit.visitNotes || 'No notes available'}</p>
                          {visit.vitals && visit.vitals.length > 0 && (
                            <div className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                              <Activity className="h-3 w-3" />
                              {visit.vitals.length} vital(s) recorded
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                IPD Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ipdDocuments.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No IPD documents uploaded</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Uploaded Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ipdDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium">{doc.fileName}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{doc.category}</Badge>
                        </TableCell>
                        <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(doc.fileUrl, '_blank')}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Patient Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Patient Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {patientDocuments.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No patient documents</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Uploaded Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patientDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium">{doc.fileName}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{doc.category}</Badge>
                        </TableCell>
                        <TableCell>{formatDate(doc.uploadedAt)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(doc.fileUrl, '_blank')}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* OPD Tests Tab */}
        <TabsContent value="opd-tests" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                OPD Lab Tests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {opdLabTests.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No OPD lab tests found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Test Name</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Doctor</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {opdLabTests.map((test) => (
                      <TableRow key={test.id}>
                        <TableCell className="font-medium">
                          {test.labTest?.name || test.testName || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {test.appointment?.scheduledAt
                            ? formatDate(test.appointment.scheduledAt)
                            : formatDate(test.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(test.status)}>
                            {test.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {test.appointment?.doctor?.name || 'N/A'}
                        </TableCell>
                        <TableCell>
                          {test.results && test.results.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedTestForView({
                                  id: test.id,
                                  name: test.labTest?.name || test.testName || 'Test'
                                });
                                setIsViewTestResultOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Results
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* OPD Surgeries Tab */}
        <TabsContent value="opd-surgeries" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scissors className="h-5 w-5" />
                OPD Surgeries
              </CardTitle>
            </CardHeader>
            <CardContent>
              {opdSurgeries.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No OPD surgeries found</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Scheduled Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Doctor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {opdSurgeries.map((surgery) => (
                      <TableRow key={surgery.id}>
                        <TableCell className="font-medium">{surgery.category}</TableCell>
                        <TableCell>{surgery.description || 'N/A'}</TableCell>
                        <TableCell>
                          {surgery.scheduledAt ? formatDate(surgery.scheduledAt) : 'Not scheduled'}
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(surgery.status)}>
                            {surgery.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {surgery.appointment?.doctor?.name || 'N/A'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* IPD Visits Tab */}
        <TabsContent value="ipd-visits" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Stethoscope className="h-5 w-5" />
                  IPD Visits
                </span>
                <Button size="sm" onClick={() => setIsVisitFormOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Visit
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.entries(visitsByDate).length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No visits recorded</h3>
                  <p className="text-gray-500">Record the first visit for this patient</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(visitsByDate).map(([date, visits]: [string, any]) => (
                    <div key={date} className="border-l-4 border-blue-500 pl-4">
                      <div className="font-bold text-lg text-gray-900 mb-3">{date}</div>
                      <div className="space-y-3">
                        {visits.map((visit: any) => (
                          <Card 
                            key={visit.id}
                            className="cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => {
                              setSelectedVisit(visit);
                              setIsVisitDetailOpen(true);
                            }}
                          >
                            <CardContent className="pt-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 text-sm text-artis-slate mb-2">
                                    <Clock className="h-4 w-4" />
                                    {formatDate(visit.visitDate || visit.createdAt)}
                                    {visit.doctor && (
                                      <>
                                        <span>•</span>
                                        <span className="text-blue-600 font-medium">{ensureDoctorPrefix(visit.doctor.name)}</span>
                                      </>
                                    )}
                                  </div>
                                  <div className="space-y-2">
                                    {visit.visitNotes && (
                                      <div>
                                        <div className="text-sm font-medium text-gray-700 mb-1">Notes:</div>
                                        <div className="text-sm text-gray-600 line-clamp-2">{visit.visitNotes}</div>
                                      </div>
                                    )}
                                    {visit.clinicalObservations && (
                                      <div>
                                        <div className="text-sm font-medium text-gray-700 mb-1">Clinical Observations:</div>
                                        <div className="text-sm text-gray-600 line-clamp-2">{visit.clinicalObservations}</div>
                                      </div>
                                    )}
                                    {visit.treatmentGiven && (
                                      <div>
                                        <div className="text-sm font-medium text-gray-700 mb-1">Treatment Given:</div>
                                        <div className="text-sm text-gray-600 line-clamp-2">{visit.treatmentGiven}</div>
                                      </div>
                                    )}
                                    {visit.vitals && visit.vitals.length > 0 && (
                                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
                                        <Activity className="h-3 w-3" />
                                        {visit.vitals.length} vital(s) recorded
                                      </div>
                                    )}
                                    <div className="text-xs text-blue-600 mt-2 font-medium">
                                      Click to view full details →
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* IPD Tests Tab */}
        <TabsContent value="ipd-tests" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <TestTube className="h-5 w-5" />
                  IPD Lab Tests
                </span>
                <Button size="sm" onClick={() => setIsAddTestFormOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Test
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ipdLabTests.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No IPD lab tests ordered</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Test Name</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ordered Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ipdLabTests.map((test) => (
                      <TableRow key={test.id}>
                        <TableCell className="font-medium">{test.testName}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{test.priority}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(test.status)}>
                            {test.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(test.orderedAt)}</TableCell>
                        <TableCell>
                          {test.resultValue ? (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setSelectedIPDTest(test);
                                setIsViewIPDTestOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Results
                            </Button>
                          ) : (
                            <span className="text-gray-400 text-sm">No results yet</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* IPD Surgeries Tab */}
        <TabsContent value="ipd-surgeries" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Scissors className="h-5 w-5" />
                  IPD Surgeries
                </span>
                <Button size="sm" onClick={() => setIsAddSurgeryFormOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Surgery
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ipdSurgeries.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No IPD surgeries scheduled</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Surgery Name</TableHead>
                      <TableHead>Priority</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Scheduled Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ipdSurgeries.map((surgery) => (
                      <TableRow key={surgery.id}>
                        <TableCell className="font-medium">{surgery.surgeryName}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{surgery.priority}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(surgery.status)}>
                            {formatSurgeryStatus(surgery.status)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {surgery.scheduledAt ? formatDate(surgery.scheduledAt) : 'Not scheduled'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {surgery.status === 'NOT_CONFIRMED' && (
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => handleConfirmSurgery(surgery.id)}
                                disabled={confirmingSurgeryId === surgery.id}
                              >
                                {confirmingSurgeryId === surgery.id ? 'Confirming...' : 'Confirm'}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditSurgeryForm(surgery)}
                            >
                              <Pencil className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedSurgery(surgery);
                                setIsSurgeryDetailOpen(true);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Details
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Surgery Detail Dialog */}
      <Dialog open={isSurgeryDetailOpen} onOpenChange={setIsSurgeryDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="surgery-detail-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="h-5 w-5 text-blue-600" />
              Surgery Details
            </DialogTitle>
          </DialogHeader>
          {selectedSurgery && (
            <div id="surgery-detail-description" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Surgery Name</div>
                  <div className="font-medium">{selectedSurgery.surgeryName}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Surgery Code</div>
                  <div>{selectedSurgery.surgeryCode || '—'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Category</div>
                  <div>{selectedSurgery.category || '—'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Priority</div>
                  <Badge variant="outline">{selectedSurgery.priority}</Badge>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Status</div>
                  <Badge className={getStatusColor(selectedSurgery.status)}>{formatSurgeryStatus(selectedSurgery.status)}</Badge>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Scheduled Date & Time</div>
                  <div>{selectedSurgery.scheduledAt ? formatDate(selectedSurgery.scheduledAt) : '—'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Estimated Duration</div>
                  <div>{selectedSurgery.estimatedDuration ? `${selectedSurgery.estimatedDuration} min` : '—'}</div>
                </div>
              </div>
              {selectedSurgery.procedureDescription && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Procedure Description</div>
                  <p className="text-sm bg-muted/50 p-3 rounded-md">{selectedSurgery.procedureDescription}</p>
                </div>
              )}
              {(selectedSurgery.preoperativeDiagnosis || selectedSurgery.postoperativeDiagnosis) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {selectedSurgery.preoperativeDiagnosis && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground mb-1">Preoperative Diagnosis</div>
                      <p className="text-sm bg-muted/50 p-3 rounded-md">{selectedSurgery.preoperativeDiagnosis}</p>
                    </div>
                  )}
                  {selectedSurgery.postoperativeDiagnosis && (
                    <div>
                      <div className="text-sm font-medium text-muted-foreground mb-1">Postoperative Diagnosis</div>
                      <p className="text-sm bg-muted/50 p-3 rounded-md">{selectedSurgery.postoperativeDiagnosis}</p>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Primary Surgeon</div>
                  <div>{selectedSurgery.primarySurgeon || selectedSurgery.primarySurgeonStaff?.name || '—'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Anesthesiologist</div>
                  <div>{selectedSurgery.anesthesiologist || '—'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Assistant Surgeon</div>
                  <div>{selectedSurgery.assistantSurgeon || '—'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground">Anesthesia Type</div>
                  <div>{selectedSurgery.anesthesiaType || '—'}</div>
                </div>
                {selectedSurgery.bloodTransfusion && (
                  <>
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Blood Units</div>
                      <div>{selectedSurgery.bloodUnits ?? '—'}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-muted-foreground">Blood Loss</div>
                      <div>{selectedSurgery.bloodLoss != null ? `${selectedSurgery.bloodLoss} ml` : '—'}</div>
                    </div>
                  </>
                )}
              </div>
              {(selectedSurgery.surgeryCost != null || selectedSurgery.anesthesiaCost != null || selectedSurgery.totalCost != null) && (
                <div className="grid grid-cols-3 gap-4 pt-2 border-t">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Surgery Cost</div>
                    <div>₹ {selectedSurgery.surgeryCost != null ? Number(selectedSurgery.surgeryCost).toFixed(2) : '—'}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Anesthesia Cost</div>
                    <div>₹ {selectedSurgery.anesthesiaCost != null ? Number(selectedSurgery.anesthesiaCost).toFixed(2) : '—'}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Total Cost</div>
                    <div>₹ {selectedSurgery.totalCost != null ? Number(selectedSurgery.totalCost).toFixed(2) : '—'}</div>
                  </div>
                </div>
              )}
              {selectedSurgery.anesthesiaNotes && (
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Anesthesia Notes</div>
                  <p className="text-sm bg-muted/50 p-3 rounded-md">{selectedSurgery.anesthesiaNotes}</p>
                </div>
              )}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (selectedSurgery) {
                      openEditSurgeryForm(selectedSurgery);
                      setIsSurgeryDetailOpen(false);
                    }
                  }}
                >
                  Edit Surgery
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Visit Form */}
      <IPDVisitForm
        isOpen={isVisitFormOpen}
        onClose={() => setIsVisitFormOpen(false)}
        onSuccess={handleVisitAdded}
        admissionId={admissionId!}
        patientName={patient.name}
      />

      {/* Add Surgery Dialog - full form */}
      <Dialog open={isAddSurgeryFormOpen} onOpenChange={setIsAddSurgeryFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="add-surgery-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="h-5 w-5 text-blue-600" />
              Add IPD Surgery
            </DialogTitle>
          </DialogHeader>
          <p id="add-surgery-description" className="text-sm text-muted-foreground">
            Schedule a surgery for this admission. Fill in the details below.
          </p>
          {(admission.surgeryName || (admission.totalPackageCost != null && admission.totalPackageCost > 0)) && (
            <div className="rounded-lg border bg-muted/50 p-3 text-sm">
              <span className="font-medium text-muted-foreground">From admission (reception):</span>
              <ul className="mt-1 list-disc list-inside">
                {admission.surgeryName && <li>Surgery name: {admission.surgeryName}</li>}
                {admission.totalPackageCost != null && admission.totalPackageCost > 0 && (
                  <li>Total package cost: ₹ {Number(admission.totalPackageCost).toFixed(2)} (room included, no GST)</li>
                )}
              </ul>
            </div>
          )}
          <form
            className="space-y-6"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!admissionId || !surgeryForm.surgeryName.trim()) {
                toast.error('Surgery name and priority are required');
                return;
              }
              setSurgerySubmitting(true);
              try {
                await ipdApi.createIPDSurgery({
                  admissionId,
                  surgeryName: surgeryForm.surgeryName.trim(),
                  surgeryCode: surgeryForm.surgeryCode.trim() || undefined,
                  category: surgeryForm.category.trim() || undefined,
                  priority: surgeryForm.priority,
                  scheduledAt: surgeryForm.scheduledAt || undefined,
                  estimatedDuration: surgeryForm.estimatedDuration ? parseInt(surgeryForm.estimatedDuration, 10) : undefined,
                  procedureDescription: surgeryForm.procedureDescription.trim() || undefined,
                  preoperativeDiagnosis: surgeryForm.preoperativeDiagnosis.trim() || undefined,
                  postoperativeDiagnosis: surgeryForm.postoperativeDiagnosis.trim() || undefined,
                  anesthesiaType: surgeryForm.anesthesiaType.trim() || undefined,
                  anesthesiaNotes: surgeryForm.anesthesiaNotes.trim() || undefined,
                  status: surgeryForm.status,
                  bloodTransfusion: surgeryForm.bloodTransfusion,
                  bloodLoss: surgeryForm.bloodLoss ? parseFloat(surgeryForm.bloodLoss) : undefined,
                  bloodUnits: surgeryForm.bloodUnits ? parseInt(surgeryForm.bloodUnits, 10) : undefined,
                  primarySurgeonId: surgeryForm.primarySurgeonId || undefined,
                  primarySurgeon: surgeryForm.primarySurgeon.trim() || undefined,
                  assistantSurgeon: surgeryForm.assistantSurgeon.trim() || undefined,
                  anesthesiologist: surgeryForm.anesthesiologist.trim() || undefined,
                  scrubNurse: surgeryForm.scrubNurse.trim() || undefined,
                  circulatingNurse: surgeryForm.circulatingNurse.trim() || undefined,
                  surgeryCost: surgeryForm.surgeryCost ? parseFloat(surgeryForm.surgeryCost) : undefined,
                  anesthesiaCost: surgeryForm.anesthesiaCost ? parseFloat(surgeryForm.anesthesiaCost) : undefined,
                  totalCost: surgeryForm.totalCost ? parseFloat(surgeryForm.totalCost) : undefined,
                });
                toast.success('Surgery scheduled successfully');
                const newPackageCost = surgeryForm.totalCost ? parseFloat(surgeryForm.totalCost) : (admission.totalPackageCost ?? undefined);
                try {
                  await ipdApi.updateAdmission(admissionId, {
                    surgeryName: surgeryForm.surgeryName.trim(),
                    totalPackageCost: newPackageCost,
                  });
                } catch (_) { /* non-blocking */ }
                setIsAddSurgeryFormOpen(false);
                setSurgeryForm({
                  surgeryName: '', surgeryCode: '', category: '', priority: 'ROUTINE', status: 'NOT_CONFIRMED', scheduledAt: '',
                  estimatedDuration: '', procedureDescription: '', preoperativeDiagnosis: '', postoperativeDiagnosis: '',
                  anesthesiaType: '', anesthesiaNotes: '', bloodTransfusion: false, bloodLoss: '', bloodUnits: '',
                  primarySurgeonId: '', primarySurgeon: '', assistantSurgeon: '', anesthesiologist: '',
                  scrubNurse: '', circulatingNurse: '', surgeryCost: '', anesthesiaCost: '', totalCost: '',
                });
                fetchPatientDetails();
              } catch (err: any) {
                toast.error(err.response?.data?.message || 'Failed to schedule surgery');
              } finally {
                setSurgerySubmitting(false);
              }
            }}
          >
            {/* Surgery Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 border-b pb-1">Surgery Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="surgery-name">Surgery Name *</Label>
                  <Input id="surgery-name" value={surgeryForm.surgeryName} onChange={(e) => setSurgeryForm((f) => ({ ...f, surgeryName: e.target.value }))} placeholder="e.g. Appendectomy" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-code">Surgery Code</Label>
                  <Input id="surgery-code" value={surgeryForm.surgeryCode} onChange={(e) => setSurgeryForm((f) => ({ ...f, surgeryCode: e.target.value }))} placeholder="Optional" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-category">Category</Label>
                  <Input id="surgery-category" value={surgeryForm.category} onChange={(e) => setSurgeryForm((f) => ({ ...f, category: e.target.value }))} placeholder="e.g. Orthopedic" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-priority">Priority *</Label>
                  <Select value={surgeryForm.priority} onValueChange={(v: 'ROUTINE' | 'URGENT' | 'STAT' | 'EMERGENCY') => setSurgeryForm((f) => ({ ...f, priority: v }))}>
                    <SelectTrigger id="surgery-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ROUTINE">Routine</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                      <SelectItem value="STAT">Stat</SelectItem>
                      <SelectItem value="EMERGENCY">Emergency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-status">Status</Label>
                  <Select value={surgeryForm.status} onValueChange={(v: 'NOT_CONFIRMED' | 'CONFIRMED' | 'NOT_REQUIRED' | 'CANCELLED') => setSurgeryForm((f) => ({ ...f, status: v }))}>
                    <SelectTrigger id="surgery-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NOT_CONFIRMED">Not confirmed</SelectItem>
                      <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                      <SelectItem value="NOT_REQUIRED">Not required</SelectItem>
                      <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="surgery-date">Scheduled Date & Time</Label>
                  <Input id="surgery-date" type="datetime-local" value={surgeryForm.scheduledAt} onChange={(e) => setSurgeryForm((f) => ({ ...f, scheduledAt: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-duration">Estimated Duration (minutes)</Label>
                  <Input id="surgery-duration" type="number" min={1} placeholder="e.g., 120" value={surgeryForm.estimatedDuration} onChange={(e) => setSurgeryForm((f) => ({ ...f, estimatedDuration: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-surgeon">Primary Surgeon</Label>
                  <DoctorSearchableSelect
                    id="surgery-surgeon"
                    doctors={doctors}
                    value={surgeryForm.primarySurgeonId}
                    onValueChange={(v) =>
                      setSurgeryForm((f) => ({
                        ...f,
                        primarySurgeonId: v,
                        primarySurgeon: doctors.find((d) => d.id === v)?.name ?? f.primarySurgeon,
                      }))
                    }
                    placeholder="Select surgeon"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-anesthesiologist">Anesthesiologist</Label>
                  <Input id="surgery-anesthesiologist" value={surgeryForm.anesthesiologist} onChange={(e) => setSurgeryForm((f) => ({ ...f, anesthesiologist: e.target.value }))} placeholder="Enter anesthesiologist name" />
                </div>
              </div>
            </div>

            {/* Procedure Description */}
            <div className="space-y-2">
              <Label htmlFor="surgery-procedure">Procedure Description</Label>
              <Textarea id="surgery-procedure" rows={3} value={surgeryForm.procedureDescription} onChange={(e) => setSurgeryForm((f) => ({ ...f, procedureDescription: e.target.value }))} placeholder="Describe the surgical procedure" />
            </div>

            {/* Diagnosis */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 border-b pb-1">Diagnosis</h4>
              <div className="space-y-2">
                <Label htmlFor="surgery-preop">Preoperative Diagnosis</Label>
                <Textarea id="surgery-preop" rows={2} value={surgeryForm.preoperativeDiagnosis} onChange={(e) => setSurgeryForm((f) => ({ ...f, preoperativeDiagnosis: e.target.value }))} placeholder="Preoperative diagnosis" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="surgery-postop">Postoperative Diagnosis</Label>
                <Textarea id="surgery-postop" rows={2} value={surgeryForm.postoperativeDiagnosis} onChange={(e) => setSurgeryForm((f) => ({ ...f, postoperativeDiagnosis: e.target.value }))} placeholder="Postoperative diagnosis (if available)" />
              </div>
            </div>

            {/* Anesthesia and Staff */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 border-b pb-1">Anesthesia and Staff</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="surgery-anesthesia-type">Anesthesia Type</Label>
                  <Input id="surgery-anesthesia-type" value={surgeryForm.anesthesiaType} onChange={(e) => setSurgeryForm((f) => ({ ...f, anesthesiaType: e.target.value }))} placeholder="e.g., General, Local, Regional" />
                </div>
                <div className="space-y-2 flex items-end pb-2">
                  <div className="flex items-center gap-2">
                    <Checkbox id="surgery-blood" checked={surgeryForm.bloodTransfusion} onCheckedChange={(checked) => setSurgeryForm((f) => ({ ...f, bloodTransfusion: !!checked }))} />
                    <Label htmlFor="surgery-blood" className="cursor-pointer">Blood Transfusion Required</Label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-assistant">Assistant Surgeon</Label>
                  <Input id="surgery-assistant" value={surgeryForm.assistantSurgeon} onChange={(e) => setSurgeryForm((f) => ({ ...f, assistantSurgeon: e.target.value }))} placeholder="Enter assistant surgeon name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-scrub">Scrub Nurse</Label>
                  <Input id="surgery-scrub" value={surgeryForm.scrubNurse} onChange={(e) => setSurgeryForm((f) => ({ ...f, scrubNurse: e.target.value }))} placeholder="Enter scrub nurse name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-circulating">Circulating Nurse</Label>
                  <Input id="surgery-circulating" value={surgeryForm.circulatingNurse} onChange={(e) => setSurgeryForm((f) => ({ ...f, circulatingNurse: e.target.value }))} placeholder="Enter circulating nurse name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-blood-loss">Blood Loss</Label>
                  <Input id="surgery-blood-loss" value={surgeryForm.bloodLoss} onChange={(e) => setSurgeryForm((f) => ({ ...f, bloodLoss: e.target.value }))} placeholder="e.g., 200ml" />
                </div>
                {surgeryForm.bloodTransfusion && (
                  <div className="space-y-2">
                    <Label htmlFor="surgery-blood-units">Blood Units</Label>
                    <Input id="surgery-blood-units" type="number" min={0} value={surgeryForm.bloodUnits} onChange={(e) => setSurgeryForm((f) => ({ ...f, bloodUnits: e.target.value }))} />
                  </div>
                )}
              </div>
            </div>

            {/* Cost Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 border-b pb-1">Cost Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="surgery-cost">Surgery Cost (₹)</Label>
                  <Input id="surgery-cost" type="number" min={0} step={0.01} value={surgeryForm.surgeryCost} onChange={(e) => setSurgeryForm((f) => ({ ...f, surgeryCost: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-anesthesia-cost">Anesthesia Cost (₹)</Label>
                  <Input id="surgery-anesthesia-cost" type="number" min={0} step={0.01} value={surgeryForm.anesthesiaCost} onChange={(e) => setSurgeryForm((f) => ({ ...f, anesthesiaCost: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="surgery-total-cost">Total Cost (₹)</Label>
                  <Input id="surgery-total-cost" type="number" min={0} step={0.01} value={surgeryForm.totalCost} onChange={(e) => setSurgeryForm((f) => ({ ...f, totalCost: e.target.value }))} placeholder="0.00" />
                </div>
              </div>
            </div>

            {/* Anesthesia Notes */}
            <div className="space-y-2">
              <Label htmlFor="surgery-anesthesia-notes">Anesthesia Notes</Label>
              <Textarea id="surgery-anesthesia-notes" rows={2} value={surgeryForm.anesthesiaNotes} onChange={(e) => setSurgeryForm((f) => ({ ...f, anesthesiaNotes: e.target.value }))} placeholder="Any notes about anesthesia" />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setIsAddSurgeryFormOpen(false)} disabled={surgerySubmitting}>Cancel</Button>
              <Button type="submit" disabled={surgerySubmitting}>{surgerySubmitting ? 'Scheduling...' : 'Schedule Surgery'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit IPD Surgery Dialog */}
      <Dialog open={isEditSurgeryFormOpen} onOpenChange={(open) => { if (!open) { setIsEditSurgeryFormOpen(false); setEditingSurgery(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="edit-surgery-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scissors className="h-5 w-5 text-blue-600" />
              Edit IPD Surgery
            </DialogTitle>
          </DialogHeader>
          <p id="edit-surgery-description" className="text-sm text-muted-foreground">
            Update surgery details below.
          </p>
          {(admission.surgeryName || (admission.totalPackageCost != null && admission.totalPackageCost > 0)) && (
            <div className="rounded-lg border bg-muted/50 p-3 text-sm">
              <span className="font-medium text-muted-foreground">From admission (reception):</span>
              <ul className="mt-1 list-disc list-inside">
                {admission.surgeryName && <li>Surgery name: {admission.surgeryName}</li>}
                {admission.totalPackageCost != null && admission.totalPackageCost > 0 && (
                  <li>Total package cost: ₹ {Number(admission.totalPackageCost).toFixed(2)} (room included, no GST)</li>
                )}
              </ul>
            </div>
          )}
          <form
            className="space-y-6"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!editingSurgery?.id || !editSurgeryForm.surgeryName.trim()) {
                toast.error('Surgery name is required');
                return;
              }
              setEditSurgerySubmitting(true);
              try {
                await ipdApi.updateIPDSurgery(editingSurgery.id, {
                  surgeryName: editSurgeryForm.surgeryName.trim(),
                  surgeryCode: editSurgeryForm.surgeryCode.trim() || undefined,
                  category: editSurgeryForm.category.trim() || undefined,
                  priority: editSurgeryForm.priority,
                  status: editSurgeryForm.status,
                  scheduledAt: editSurgeryForm.scheduledAt || undefined,
                  estimatedDuration: editSurgeryForm.estimatedDuration ? parseInt(editSurgeryForm.estimatedDuration, 10) : undefined,
                  procedureDescription: editSurgeryForm.procedureDescription.trim() || undefined,
                  preoperativeDiagnosis: editSurgeryForm.preoperativeDiagnosis.trim() || undefined,
                  postoperativeDiagnosis: editSurgeryForm.postoperativeDiagnosis.trim() || undefined,
                  anesthesiaType: editSurgeryForm.anesthesiaType.trim() || undefined,
                  anesthesiaNotes: editSurgeryForm.anesthesiaNotes.trim() || undefined,
                  bloodTransfusion: editSurgeryForm.bloodTransfusion,
                  bloodLoss: editSurgeryForm.bloodLoss ? parseFloat(editSurgeryForm.bloodLoss) : undefined,
                  bloodUnits: editSurgeryForm.bloodUnits ? parseInt(editSurgeryForm.bloodUnits, 10) : undefined,
                  primarySurgeonId: editSurgeryForm.primarySurgeonId || undefined,
                  primarySurgeon: editSurgeryForm.primarySurgeon.trim() || undefined,
                  assistantSurgeon: editSurgeryForm.assistantSurgeon.trim() || undefined,
                  anesthesiologist: editSurgeryForm.anesthesiologist.trim() || undefined,
                  scrubNurse: editSurgeryForm.scrubNurse.trim() || undefined,
                  circulatingNurse: editSurgeryForm.circulatingNurse.trim() || undefined,
                  surgeryCost: editSurgeryForm.surgeryCost ? parseFloat(editSurgeryForm.surgeryCost) : undefined,
                  anesthesiaCost: editSurgeryForm.anesthesiaCost ? parseFloat(editSurgeryForm.anesthesiaCost) : undefined,
                  totalCost: editSurgeryForm.totalCost ? parseFloat(editSurgeryForm.totalCost) : undefined,
                });
                toast.success('Surgery updated successfully');
                if (admissionId) {
                  const newPackageCost = editSurgeryForm.totalCost ? parseFloat(editSurgeryForm.totalCost) : (admission.totalPackageCost ?? undefined);
                  try {
                    await ipdApi.updateAdmission(admissionId, {
                      surgeryName: editSurgeryForm.surgeryName.trim(),
                      totalPackageCost: newPackageCost,
                    });
                  } catch (_) { /* non-blocking */ }
                }
                setIsEditSurgeryFormOpen(false);
                setEditingSurgery(null);
                fetchPatientDetails();
              } catch (err: any) {
                toast.error(err.response?.data?.message || 'Failed to update surgery');
              } finally {
                setEditSurgerySubmitting(false);
              }
            }}
          >
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 border-b pb-1">Surgery Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-name">Surgery Name *</Label>
                  <Input id="edit-surgery-name" value={editSurgeryForm.surgeryName} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, surgeryName: e.target.value }))} placeholder="e.g. Appendectomy" required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-code">Surgery Code</Label>
                  <Input id="edit-surgery-code" value={editSurgeryForm.surgeryCode} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, surgeryCode: e.target.value }))} placeholder="Optional" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-category">Category</Label>
                  <Input id="edit-surgery-category" value={editSurgeryForm.category} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, category: e.target.value }))} placeholder="e.g. Orthopedic" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-priority">Priority *</Label>
                  <Select value={editSurgeryForm.priority} onValueChange={(v: 'ROUTINE' | 'URGENT' | 'STAT' | 'EMERGENCY') => setEditSurgeryForm((f) => ({ ...f, priority: v }))}>
                    <SelectTrigger id="edit-surgery-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ROUTINE">Routine</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                      <SelectItem value="STAT">Stat</SelectItem>
                      <SelectItem value="EMERGENCY">Emergency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-status">Status</Label>
                  <Select value={editSurgeryForm.status} onValueChange={(v: 'NOT_CONFIRMED' | 'CONFIRMED' | 'NOT_REQUIRED' | 'CANCELLED') => setEditSurgeryForm((f) => ({ ...f, status: v }))}>
                    <SelectTrigger id="edit-surgery-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NOT_CONFIRMED">Not confirmed</SelectItem>
                      <SelectItem value="CONFIRMED">Confirmed</SelectItem>
                      <SelectItem value="NOT_REQUIRED">Not required</SelectItem>
                      <SelectItem value="CANCELLED">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="edit-surgery-date">Scheduled Date & Time</Label>
                  <Input id="edit-surgery-date" type="datetime-local" value={editSurgeryForm.scheduledAt} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, scheduledAt: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-duration">Estimated Duration (minutes)</Label>
                  <Input id="edit-surgery-duration" type="number" min={1} placeholder="e.g., 120" value={editSurgeryForm.estimatedDuration} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, estimatedDuration: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-surgeon">Primary Surgeon</Label>
                  <DoctorSearchableSelect
                    id="edit-surgery-surgeon"
                    doctors={doctors}
                    value={editSurgeryForm.primarySurgeonId}
                    onValueChange={(v) =>
                      setEditSurgeryForm((f) => ({
                        ...f,
                        primarySurgeonId: v,
                        primarySurgeon: doctors.find((d) => d.id === v)?.name ?? f.primarySurgeon,
                      }))
                    }
                    placeholder="Select surgeon"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-anesthesiologist">Anesthesiologist</Label>
                  <Input id="edit-surgery-anesthesiologist" value={editSurgeryForm.anesthesiologist} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, anesthesiologist: e.target.value }))} placeholder="Enter anesthesiologist name" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-surgery-procedure">Procedure Description</Label>
              <Textarea id="edit-surgery-procedure" rows={3} value={editSurgeryForm.procedureDescription} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, procedureDescription: e.target.value }))} placeholder="Describe the surgical procedure" />
            </div>
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 border-b pb-1">Diagnosis</h4>
              <div className="space-y-2">
                <Label htmlFor="edit-surgery-preop">Preoperative Diagnosis</Label>
                <Textarea id="edit-surgery-preop" rows={2} value={editSurgeryForm.preoperativeDiagnosis} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, preoperativeDiagnosis: e.target.value }))} placeholder="Preoperative diagnosis" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-surgery-postop">Postoperative Diagnosis</Label>
                <Textarea id="edit-surgery-postop" rows={2} value={editSurgeryForm.postoperativeDiagnosis} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, postoperativeDiagnosis: e.target.value }))} placeholder="Postoperative diagnosis (if available)" />
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 border-b pb-1">Anesthesia and Staff</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-anesthesia-type">Anesthesia Type</Label>
                  <Input id="edit-surgery-anesthesia-type" value={editSurgeryForm.anesthesiaType} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, anesthesiaType: e.target.value }))} placeholder="e.g., General, Local, Regional" />
                </div>
                <div className="space-y-2 flex items-end pb-2">
                  <div className="flex items-center gap-2">
                    <Checkbox id="edit-surgery-blood" checked={editSurgeryForm.bloodTransfusion} onCheckedChange={(checked) => setEditSurgeryForm((f) => ({ ...f, bloodTransfusion: !!checked }))} />
                    <Label htmlFor="edit-surgery-blood" className="cursor-pointer">Blood Transfusion Required</Label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-assistant">Assistant Surgeon</Label>
                  <Input id="edit-surgery-assistant" value={editSurgeryForm.assistantSurgeon} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, assistantSurgeon: e.target.value }))} placeholder="Enter assistant surgeon name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-scrub">Scrub Nurse</Label>
                  <Input id="edit-surgery-scrub" value={editSurgeryForm.scrubNurse} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, scrubNurse: e.target.value }))} placeholder="Enter scrub nurse name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-circulating">Circulating Nurse</Label>
                  <Input id="edit-surgery-circulating" value={editSurgeryForm.circulatingNurse} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, circulatingNurse: e.target.value }))} placeholder="Enter circulating nurse name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-blood-loss">Blood Loss</Label>
                  <Input id="edit-surgery-blood-loss" value={editSurgeryForm.bloodLoss} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, bloodLoss: e.target.value }))} placeholder="e.g., 200ml" />
                </div>
                {editSurgeryForm.bloodTransfusion && (
                  <div className="space-y-2">
                    <Label htmlFor="edit-surgery-blood-units">Blood Units</Label>
                    <Input id="edit-surgery-blood-units" type="number" min={0} value={editSurgeryForm.bloodUnits} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, bloodUnits: e.target.value }))} />
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-900 border-b pb-1">Cost Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-cost">Surgery Cost (₹)</Label>
                  <Input id="edit-surgery-cost" type="number" min={0} step={0.01} value={editSurgeryForm.surgeryCost} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, surgeryCost: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-anesthesia-cost">Anesthesia Cost (₹)</Label>
                  <Input id="edit-surgery-anesthesia-cost" type="number" min={0} step={0.01} value={editSurgeryForm.anesthesiaCost} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, anesthesiaCost: e.target.value }))} placeholder="0.00" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-surgery-total-cost">Total Cost (₹)</Label>
                  <Input id="edit-surgery-total-cost" type="number" min={0} step={0.01} value={editSurgeryForm.totalCost} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, totalCost: e.target.value }))} placeholder="0.00" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-surgery-anesthesia-notes">Anesthesia Notes</Label>
              <Textarea id="edit-surgery-anesthesia-notes" rows={2} value={editSurgeryForm.anesthesiaNotes} onChange={(e) => setEditSurgeryForm((f) => ({ ...f, anesthesiaNotes: e.target.value }))} placeholder="Any notes about anesthesia" />
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => { setIsEditSurgeryFormOpen(false); setEditingSurgery(null); }} disabled={editSurgerySubmitting}>Cancel</Button>
              <Button type="submit" disabled={editSurgerySubmitting}>{editSurgerySubmitting ? 'Saving...' : 'Save Changes'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Visit Detail Dialog */}
      <Dialog open={isVisitDetailOpen} onOpenChange={setIsVisitDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-blue-600" />
              Visit Details
            </DialogTitle>
          </DialogHeader>
          {selectedVisit && (
            <div className="space-y-4">
              {/* Visit Header */}
              <div className="flex items-center justify-between pb-4 border-b">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm text-artis-slate">
                      <Calendar className="h-4 w-4" />
                      {formatDate(selectedVisit.visitDate || selectedVisit.createdAt)}
                    </div>
                    {selectedVisit.doctor && (
                      <div className="flex items-center gap-2 mt-1">
                        <User className="h-4 w-4 text-gray-400" />
                        <span className="font-medium">{ensureDoctorPrefix(selectedVisit.doctor.name)}</span>
                        {selectedVisit.doctor.specialisation && (
                          <span className="text-sm text-artis-slate">
                            ({selectedVisit.doctor.specialisation})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Visit Notes */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Visit Notes</h4>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                  {selectedVisit.visitNotes || 'No notes available'}
                </p>
              </div>

              {/* Clinical Observations */}
              {selectedVisit.clinicalObservations && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Clinical Observations</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                    {selectedVisit.clinicalObservations}
                  </p>
                </div>
              )}

              {/* Treatment Given */}
              {selectedVisit.treatmentGiven && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Treatment Given</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                    {selectedVisit.treatmentGiven}
                  </p>
                </div>
              )}

              {/* Medication Changes */}
              {selectedVisit.medicationChanges && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Medication Changes</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                    {selectedVisit.medicationChanges}
                  </p>
                </div>
              )}

              {/* Patient Response */}
              {selectedVisit.patientResponse && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Patient Response</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                    {selectedVisit.patientResponse}
                  </p>
                </div>
              )}

              {/* Next Visit Plan */}
              {selectedVisit.nextVisitPlan && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Next Visit Plan</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                    {selectedVisit.nextVisitPlan}
                  </p>
                </div>
              )}

              {/* Vitals */}
              {selectedVisit.vitals && selectedVisit.vitals.length > 0 && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Vitals</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {selectedVisit.vitals.map((vital: any, index: number) => {
                      const getVitalIcon = (type: string) => {
                        switch (type) {
                          case 'BP_SYSTOLIC':
                          case 'BP_DIASTOLIC':
                            return <Activity className="h-4 w-4 text-blue-600" />;
                          case 'HEART_RATE':
                            return <Heart className="h-4 w-4 text-red-600" />;
                          case 'TEMPERATURE':
                            return <Thermometer className="h-4 w-4 text-orange-600" />;
                          default:
                            return <Activity className="h-4 w-4 text-gray-600" />;
                        }
                      };

                      const getVitalLabel = (type: string) => {
                        switch (type) {
                          case 'BP_SYSTOLIC': return 'BP Systolic';
                          case 'BP_DIASTOLIC': return 'BP Diastolic';
                          case 'HEART_RATE': return 'Heart Rate';
                          case 'TEMPERATURE': return 'Temperature';
                          case 'WEIGHT': return 'Weight';
                          case 'HEIGHT': return 'Height';
                          case 'SPO2': return 'SpO2';
                          case 'RESPIRATORY_RATE': return 'Respiratory Rate';
                          default: return type;
                        }
                      };

                      return (
                        <div key={index} className="bg-blue-50 p-3 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            {getVitalIcon(vital.type)}
                            <span className="text-xs font-medium text-gray-700">
                              {getVitalLabel(vital.type)}
                            </span>
                          </div>
                          <div className="text-sm font-semibold text-gray-900">
                            {vital.value} {vital.unit || ''}
                          </div>
                          {vital.notes && (
                            <div className="text-xs text-artis-slate mt-1">{vital.notes}</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* OPD Test Results Dialog */}
      <Dialog open={isViewTestResultOpen} onOpenChange={setIsViewTestResultOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Test Result - {selectedTestForView?.name}</DialogTitle>
          </DialogHeader>
          {selectedTestForView && (
            <ViewTestResult
              appointmentLabTestId={selectedTestForView.id}
              testName={selectedTestForView.name}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* IPD Test Results Dialog */}
      <Dialog open={isViewIPDTestOpen} onOpenChange={setIsViewIPDTestOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TestTube className="h-5 w-5 text-blue-600" />
              Test Result - {selectedIPDTest?.testName || 'IPD Lab Test'}
            </DialogTitle>
          </DialogHeader>
          {selectedIPDTest && (
            <div className="space-y-4">
              {/* Test Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-500">Test Name</div>
                  <div className="text-base font-semibold text-gray-900">{selectedIPDTest.testName}</div>
                </div>
                {selectedIPDTest.testCode && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Test Code</div>
                    <div className="text-base text-gray-900">{selectedIPDTest.testCode}</div>
                  </div>
                )}
                <div>
                  <div className="text-sm font-medium text-gray-500">Status</div>
                  <Badge className={getStatusColor(selectedIPDTest.status)}>
                    {selectedIPDTest.status}
                  </Badge>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Priority</div>
                  <Badge variant="outline">{selectedIPDTest.priority}</Badge>
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-500">Ordered Date</div>
                  <div className="text-base text-gray-900">{formatDate(selectedIPDTest.orderedAt)}</div>
                </div>
                {selectedIPDTest.completedAt && (
                  <div>
                    <div className="text-sm font-medium text-gray-500">Completed Date</div>
                    <div className="text-base text-gray-900">{formatDate(selectedIPDTest.completedAt)}</div>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Results</h3>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <div className="text-sm font-medium text-gray-500 mb-1">Result Value</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {selectedIPDTest.resultValue}
                        {selectedIPDTest.resultUnit && ` ${selectedIPDTest.resultUnit}`}
                      </div>
                    </div>
                    {selectedIPDTest.normalRange && (
                      <div>
                        <div className="text-sm font-medium text-gray-500 mb-1">Normal Range</div>
                        <div className="text-base text-gray-700">{selectedIPDTest.normalRange}</div>
                      </div>
                    )}
                  </div>
                  {selectedIPDTest.abnormalFlag && (
                    <div className="mt-2">
                      <Badge variant="destructive" className="mb-2">Abnormal Result</Badge>
                    </div>
                  )}
                  {selectedIPDTest.resultNotes && (
                    <div className="mt-4">
                      <div className="text-sm font-medium text-gray-500 mb-1">Notes</div>
                      <div className="text-sm text-gray-700 bg-white p-3 rounded">
                        {selectedIPDTest.resultNotes}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Test Instructions */}
              {(selectedIPDTest.instructions || selectedIPDTest.specialInstructions || selectedIPDTest.fastingRequired) && (
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Instructions</h3>
                  <div className="space-y-2">
                    {selectedIPDTest.instructions && (
                      <div>
                        <div className="text-sm font-medium text-gray-500 mb-1">Instructions</div>
                        <div className="text-sm text-gray-700">{selectedIPDTest.instructions}</div>
                      </div>
                    )}
                    {selectedIPDTest.fastingRequired && (
                      <div>
                        <div className="text-sm font-medium text-gray-500 mb-1">Fasting Required</div>
                        <div className="text-sm text-gray-700">
                          Yes
                          {selectedIPDTest.fastingHours && ` (${selectedIPDTest.fastingHours} hours)`}
                        </div>
                      </div>
                    )}
                    {selectedIPDTest.specialInstructions && (
                      <div>
                        <div className="text-sm font-medium text-gray-500 mb-1">Special Instructions</div>
                        <div className="text-sm text-gray-700">{selectedIPDTest.specialInstructions}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Attachments */}
              {selectedIPDTest.attachments && selectedIPDTest.attachments.length > 0 && (
                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Attachments</h3>
                  <div className="space-y-2">
                    {selectedIPDTest.attachments.map((attachment: any) => (
                      <div key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{attachment.fileName}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(attachment.fileUrl, '_blank')}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add IPD Lab Test Dialog */}
      <Dialog open={isAddTestFormOpen} onOpenChange={setIsAddTestFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TestTube className="h-5 w-5 text-blue-600" />
              Add IPD Lab Test
            </DialogTitle>
          </DialogHeader>
          <IPDLabTestForm
            admissionId={admissionId!}
            patientName={patient?.name || ''}
            onSuccess={() => {
              setIsAddTestFormOpen(false);
              fetchPatientDetails();
            }}
            onCancel={() => setIsAddTestFormOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// IPD Lab Test Form Component
function IPDLabTestForm({
  admissionId,
  patientName: _patientName,
  onSuccess,
  onCancel
}: {
  admissionId: string;
  patientName: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [selectedLabTestId, setSelectedLabTestId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    testName: '',
    testCode: '',
    category: '',
    priority: 'ROUTINE' as 'ROUTINE' | 'URGENT' | 'STAT',
    instructions: '',
    fastingRequired: false,
    fastingHours: '',
    specialInstructions: '',
    testCost: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch available lab tests (for LabTestSearch component to use)
  useQuery<any>({
    queryKey: ['lab-tests'],
    queryFn: async () => {
      const response = await labApi.getLabTests();
      return response.data?.data;
    },
  });

  // Handle test selection from dropdown
  const handleTestSelect = (test: { id: string; name: string; code?: string; category?: string; charge?: number }) => {
    setSelectedLabTestId(test.id);
    setFormData(prev => ({
      ...prev,
      testName: test.name,
      testCode: test.code || '',
      category: test.category || '',
      testCost: test.charge?.toString() || '',
    }));
  };

  // Handle manual test name input
  const handleManualTestNameChange = (value: string) => {
    // If user manually changes test name, clear the selected test ID
    if (value !== formData.testName) {
      setSelectedLabTestId(null);
    }
    setFormData(prev => ({ ...prev, testName: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.testName.trim()) {
      toast.error('Test name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      await ipdApi.createIPDLabTest({
        admissionId,
        testName: formData.testName,
        testCode: formData.testCode || undefined,
        category: formData.category || undefined,
        priority: formData.priority,
        instructions: formData.instructions || undefined,
        fastingRequired: formData.fastingRequired,
        fastingHours: formData.fastingHours ? parseInt(formData.fastingHours) : undefined,
        specialInstructions: formData.specialInstructions || undefined,
        testCost: formData.testCost ? parseFloat(formData.testCost) : undefined,
        labTestId: selectedLabTestId || undefined,
      });
      toast.success('Lab test ordered successfully');
      onSuccess();
    } catch (error: any) {
      console.error('Error creating IPD lab test:', error);
      toast.error(error.response?.data?.message || 'Failed to create lab test');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        <div>
          <Label>Select Test from Catalog</Label>
          <LabTestSearch
            onTestSelect={handleTestSelect}
            placeholder="Search and select a test..."
            className="w-full"
          />
          {selectedLabTestId && (
            <p className="text-sm text-green-600 mt-1">
              ✓ Test selected from catalog
            </p>
          )}
        </div>

        <div className="text-sm text-artis-slate text-center">OR</div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="testName">Test Name *</Label>
            <Input
              id="testName"
              value={formData.testName}
              onChange={(e) => handleManualTestNameChange(e.target.value)}
              placeholder="Enter test name manually"
              required
              disabled={isSubmitting}
            />
          </div>
        <div>
          <Label htmlFor="testCode">Test Code</Label>
          <Input
            id="testCode"
            value={formData.testCode}
            onChange={(e) => setFormData(prev => ({ ...prev, testCode: e.target.value }))}
            placeholder="Enter test code"
            disabled={isSubmitting}
          />
        </div>
        <div>
          <Label htmlFor="category">Category</Label>
          <Input
            id="category"
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            placeholder="e.g., Blood Test, Urine Test"
            disabled={isSubmitting}
          />
        </div>
        <div>
          <Label htmlFor="priority">Priority *</Label>
          <Select
            value={formData.priority}
            onValueChange={(value: 'ROUTINE' | 'URGENT' | 'STAT') =>
              setFormData(prev => ({ ...prev, priority: value }))
            }
            disabled={isSubmitting}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ROUTINE">Routine</SelectItem>
              <SelectItem value="URGENT">Urgent</SelectItem>
              <SelectItem value="STAT">STAT</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="testCost">Test Cost</Label>
          <Input
            id="testCost"
            type="number"
            step="0.01"
            value={formData.testCost}
            onChange={(e) => setFormData(prev => ({ ...prev, testCost: e.target.value }))}
            placeholder="0.00"
            disabled={isSubmitting}
          />
        </div>
        <div className="flex items-center gap-4 pt-6">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="fastingRequired"
              checked={formData.fastingRequired}
              onChange={(e) => setFormData(prev => ({ ...prev, fastingRequired: e.target.checked }))}
              disabled={isSubmitting}
              className="rounded"
            />
            <Label htmlFor="fastingRequired" className="font-normal cursor-pointer">
              Fasting Required
            </Label>
          </div>
          {formData.fastingRequired && (
            <div className="flex-1">
              <Input
                type="number"
                value={formData.fastingHours}
                onChange={(e) => setFormData(prev => ({ ...prev, fastingHours: e.target.value }))}
                placeholder="Hours"
                disabled={isSubmitting}
                className="w-24"
              />
            </div>
          )}
        </div>
        </div>
      </div>

      <div>
        <Label htmlFor="instructions">Instructions</Label>
        <Textarea
          id="instructions"
          value={formData.instructions}
          onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
          placeholder="General instructions for the test"
          rows={3}
          disabled={isSubmitting}
        />
      </div>

      <div>
        <Label htmlFor="specialInstructions">Special Instructions</Label>
        <Textarea
          id="specialInstructions"
          value={formData.specialInstructions}
          onChange={(e) => setFormData(prev => ({ ...prev, specialInstructions: e.target.value }))}
          placeholder="Any special instructions or notes"
          rows={2}
          disabled={isSubmitting}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Ordering...' : 'Order Test'}
        </Button>
      </div>
    </form>
  );
}

